import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Users, Maximize2, Tag } from "lucide-react";
import RoomImageCarousel from "@/components/RoomImageCarousel";

const Rooms = () => {
  const { t, lang } = useLang();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/rooms"),
      api.get("/promotions/active").catch(() => ({ data: { promotions: [] } })),
    ])
      .then(([roomsRes, promoRes]) => {
        if (cancelled) return;
        setRooms(roomsRes.data);
        setPromotions(promoRes.data?.promotions || []);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Map room slug -> best promo (highest discount)
  const promoBySlug = useMemo(() => {
    const map = {};
    promotions.forEach((p) => {
      (p.rooms || []).forEach((slug) => {
        const cur = map[slug];
        if (!cur || (p.discount_pct || 0) > (cur.discount_pct || 0)) {
          map[slug] = p;
        }
      });
    });
    return map;
  }, [promotions]);

  return (
    <section id="rooms" className="py-20 md:py-28 bg-white" data-testid="rooms-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12">
        <div className="max-w-2xl mb-8">
          <p className="text-overline mb-3">{t.rooms.overline}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#1E3A5F] leading-tight mb-4">{t.rooms.title}</h2>
          <p className="text-[#635F59] leading-relaxed">{t.rooms.subtitle}</p>
        </div>

        {/* CTA Group Mode — visible above the grid for families/groups */}
        <a
          href="/booking"
          data-testid="cta-group-booking"
          className="group block mb-10 bg-gradient-to-r from-[#AC5D45]/10 via-[#E3A857]/10 to-[#AC5D45]/10 hover:from-[#AC5D45]/20 hover:to-[#AC5D45]/20 border-2 border-[#AC5D45]/40 hover:border-[#AC5D45] rounded-2xl p-5 md:p-6 transition-all"
        >
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-3xl md:text-4xl shrink-0">👨‍👩‍👧‍👦</div>
            <div className="flex-1 min-w-[260px]">
              <h3 className="font-display text-lg md:text-xl text-[#1E3A5F] mb-0.5">
                Prenoti per <strong className="text-[#AC5D45]">famiglia o gruppo</strong>?
              </h3>
              <p className="text-sm text-[#635F59]">
                Scegli più camere insieme con un'<strong>unica prenotazione</strong>. Stesse date, intestatari diversi se vuoi, una sola ricevuta.
              </p>
            </div>
            <span className="bg-[#AC5D45] group-hover:bg-[#8A4936] text-white px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap transition-colors">
              Crea gruppo →
            </span>
          </div>
        </a>

        {loading ? (
          <div className="text-[#635F59]">{t.common.loading}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
            {rooms.map((room, idx) => {
              const promo = promoBySlug[room.slug];
              const typeLabel = promo
                ? {
                    last_minute: t.promo.type_last_minute,
                    seasonal: t.promo.type_seasonal,
                    special: t.promo.type_special,
                  }[promo.type] || t.promo.type_special
                : null;
              // Lowest available price for "Da €X / notte": prefer min_price
              // (computed backend-side from base + seasonal overrides), then
              // apply any active promo discount on top of it.
              const lowestBase = Number(room.min_price ?? room.price_per_night);
              const discountedPrice =
                promo && promo.discount_pct
                  ? Math.round(lowestBase * (1 - promo.discount_pct / 100))
                  : null;
              const displayPrice = discountedPrice ?? lowestBase;
              return (
              <article
                key={room.id}
                data-testid={`room-card-${room.slug}`}
                className={`group bg-[#FDFBF7] rounded-xl overflow-hidden border transition-all duration-500 hover:shadow-xl flex flex-col h-full ${
                  promo ? "border-fuchsia-300 ring-1 ring-fuchsia-200/50" : "border-[#E8E2D9]"
                }`}
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <RoomImageCarousel
                  images={room.images || []}
                  alt={room.name?.[lang] || room.name?.it}
                  testIdSlug={room.slug}
                  badge={
                    <>
                      {promo && (
                        <div
                          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold uppercase tracking-wider shadow-lg shadow-fuchsia-500/30"
                          data-testid={`promo-badge-${room.slug}`}
                          title={promo.label}
                        >
                          <Tag className="w-3 h-3" />
                          {typeLabel}
                          {promo.discount_pct ? <span>· -{Math.round(promo.discount_pct)}%</span> : null}
                        </div>
                      )}
                      {!promo && lowestBase < Number(room.price_per_night) && (
                        <div
                          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#AC5D45] text-white text-[10px] font-bold uppercase tracking-wider shadow-lg shadow-[#AC5D45]/30 animate-pulse"
                          data-testid={`offer-badge-${room.slug}`}
                          title={`Tariffa scontata: da €${lowestBase} (base €${room.price_per_night})`}
                        >
                          🔥 Offerta
                        </div>
                      )}
                    </>
                  }
                />
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-2xl text-[#1E3A5F] mb-2 min-h-[3.5rem]">{room.name?.[lang] || room.name?.it}</h3>
                  <p className="text-sm text-[#635F59] mb-4 leading-relaxed line-clamp-3 min-h-[3.75rem]">{room.short_description?.[lang] || room.short_description?.it}</p>
                  <div className="flex items-center gap-4 text-xs text-[#635F59] mb-5">
                    <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{room.capacity} {t.rooms.capacity}</span>
                    <span className="flex items-center gap-1.5"><Maximize2 className="w-3.5 h-3.5" />{room.size_sqm} {t.rooms.sqm}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-[#E8E2D9]/60">
                    <span className="text-[#1E3A5F] font-semibold">
                      <span className="text-xs text-[#635F59] font-normal mr-1">{t.rooms.from}</span>
                      {discountedPrice != null ? (
                        <>
                          <span className="text-[#635F59] line-through font-normal text-sm mr-1.5">€{lowestBase}</span>
                          <span className="text-fuchsia-700">€{discountedPrice}</span>
                        </>
                      ) : (
                        <>€{lowestBase}</>
                      )}
                      <span className="text-xs text-[#635F59] font-normal ml-1">{t.rooms.per_night}</span>
                    </span>
                    <Button
                      asChild
                      data-testid={`book-room-${room.slug}`}
                      className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-9 px-4 text-sm"
                    >
                      <Link to={`/booking?room=${room.slug}`}>{t.rooms.book}</Link>
                    </Button>
                  </div>
                </div>
              </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default Rooms;
